import React, { useState, useEffect } from 'react';
import NewStudentsSelection from './NewStudentsSelection';
import SimpleMappingSelection from './SimpleMappingSelection';
import ExistingStudentsSelection from './ExistingStudentsSelection';

const StudentSelectionStep = ({ workflowData, setWorkflowData, schoolid }) => {
  // States for existing students and simple transitions
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sourceClasses, setSourceClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Grade levels for filtering
  const gradeLevels = [
    { value: "all", label: "All Grades" },
    { value: "grade_level1", label: "Junior Infants" },
    { value: "grade_level2", label: "Senior Infants" },
    { value: "grade_level3", label: "1st Class" },
    { value: "grade_level4", label: "2nd Class" },
    { value: "grade_level5", label: "3rd Class" },
    { value: "grade_level6", label: "4th Class" },
    { value: "grade_level7", label: "5th Class" },
    { value: "grade_level8", label: "6th Class" }
  ];

  useEffect(() => {
    console.log("Debug - StudentSelectionStep useEffect triggered:", {
      populationMethod: workflowData.populationMethod,
      schoolid,
      hasGetStudentsBySchool: !!workflowData.getStudentsBySchool,
      hasGetClassesBySchoolId: !!workflowData.getClassesBySchoolId
    });
    
    if ((workflowData.populationMethod === 'complex' || workflowData.populationMethod === 'simple') && schoolid) {
      loadStudentsAndClasses();
    }
  }, [workflowData.populationMethod, schoolid]);

  // Helper function to get academic year from date
  const getAcademicYearFromDate = (dateString) => {
    if (!dateString) return "older";
    
    const date = new Date(dateString);
    const aug1_2022 = new Date('2022-08-01');
    const aug1_2023 = new Date('2023-08-01');
    const aug1_2024 = new Date('2024-08-01');
    const aug1_2025 = new Date('2025-08-01');
    
    if (date >= aug1_2024 && date < aug1_2025) return "2024/25";
    if (date >= aug1_2023 && date < aug1_2024) return "2023/24";
    if (date >= aug1_2022 && date < aug1_2023) return "2022/23";
    return "older";
  };

  const loadStudentsAndClasses = async () => {
    setLoading(true);
    setError("");
    try {
      const getStudentsBySchool = workflowData.getStudentsBySchool;
      const getClassesBySchoolId = workflowData.getClassesBySchoolId;
      
      if (getStudentsBySchool && getClassesBySchoolId) {
        const [studentsResponse, classesResponse] = await Promise.all([
          getStudentsBySchool(schoolid),
          getClassesBySchoolId(schoolid)
        ]);

        // Transform student data
        const transformedStudents = (studentsResponse || []).map(student => {
          const gradeLabel = student.class ? getGradeLabelFromClass(student.class) : 'Unknown Grade';
          const gradeBoolean = student.class ? getGradeBooleanFromClass(student.class) : 'unknown';
          
          return {
            id: student.id,
            name: student.name,
            firstName: student.firstName,
            lastName: student.lastName,
            classId: student.classId,
            className: student.class?.name || 'Unknown Class',
            grade: gradeLabel,
            gradeBoolean: gradeBoolean
          };
        }).sort((a, b) => {
          const nameA = (a.firstName && a.lastName) ? `${a.firstName} ${a.lastName}` : a.name || '';
          const nameB = (b.firstName && b.lastName) ? `${b.firstName} ${b.lastName}` : b.name || '';
          return nameA.toLowerCase().localeCompare(nameB.toLowerCase());
        });

        // Transform class data
        const transformedClasses = (classesResponse || []).map(classItem => {
          const gradeLabel = getGradeLabelFromClass(classItem);
          const gradeBoolean = getGradeBooleanFromClass(classItem);
          const academicYear = getAcademicYearFromDate(classItem.date_updated);
          
          return {
            id: classItem.id,
            name: classItem.name,
            grade: gradeLabel,
            gradeBoolean: gradeBoolean,
            academicYear: academicYear,
            active: classItem.active,
            trial_class: classItem.trial_class,
            date_updated: classItem.date_updated,
            studentCount: (studentsResponse || []).filter(s => s.classId === classItem.id).length
          };
        }).sort((a, b) => a.name.localeCompare(b.name));

        setStudents(transformedStudents);
        setClasses(transformedClasses);
        
        // For simple transitions, separate source classes (older academic years)
        if (workflowData.populationMethod === 'simple') {
          // Don't filter by target academic year - let user choose independently
          const sourceClassesList = transformedClasses.filter(classItem => 
            classItem.studentCount > 0 // Only classes with students
          );
          setSourceClasses(sourceClassesList);
          
          // Store source classes in workflow data for later use in confirmation
          setWorkflowData(prev => ({
            ...prev,
            sourceClasses: sourceClassesList
          }));
        }

      } else {
        setError("Missing API functions. Please check configuration.");
      }
    } catch (err) {
      setError("Failed to load students and classes. Please try again.");
      console.error("Error loading data:", err);
    } finally {
      setLoading(false);
    }
  };

  const getGradeLabelFromClass = (classItem) => {
    if (classItem.grade_level1) return "Junior Infants";
    if (classItem.grade_level2) return "Senior Infants";
    if (classItem.grade_level3) return "1st Class";
    if (classItem.grade_level4) return "2nd Class";
    if (classItem.grade_level5) return "3rd Class";
    if (classItem.grade_level6) return "4th Class";
    if (classItem.grade_level7) return "5th Class";
    if (classItem.grade_level8) return "6th Class";
    return "Unknown Grade";
  };

  const getGradeBooleanFromClass = (classItem) => {
    if (classItem.grade_level1) return "grade_level1";
    if (classItem.grade_level2) return "grade_level2";
    if (classItem.grade_level3) return "grade_level3";
    if (classItem.grade_level4) return "grade_level4";
    if (classItem.grade_level5) return "grade_level5";
    if (classItem.grade_level6) return "grade_level6";
    if (classItem.grade_level7) return "grade_level7";
    if (classItem.grade_level8) return "grade_level8";
    return "unknown";
  };

  // Render appropriate component based on population method
  if (workflowData.populationMethod === 'new') {
    return (
      <NewStudentsSelection 
        workflowData={workflowData}
        setWorkflowData={setWorkflowData}
        error={error}
        setError={setError}
      />
    );
  }

  if (workflowData.populationMethod === 'simple') {
    return (
      <SimpleMappingSelection 
        workflowData={workflowData}
        setWorkflowData={setWorkflowData}
        sourceClasses={sourceClasses}
        error={error}
        loading={loading}
        gradeLevels={gradeLevels}
      />
    );
  }

  // Default to complex (existing students)
  return (
    <ExistingStudentsSelection 
      workflowData={workflowData}
      setWorkflowData={setWorkflowData}
      students={students}
      classes={classes}
      error={error}
      loading={loading}
      gradeLevels={gradeLevels}
    />
  );
};

export default StudentSelectionStep;